import React, { useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { Box, FormControl, FormHelperText, FormLabel } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { createTicketMentionSuggestion } from '../../lib/ticketMentionSuggestion';

/**
 * Comment composer with TipTap @mention suggestions.
 * @param {{
 *   label?: string,
 *   value: string,
 *   onChange: (html: string) => void,
 *   mentionItems?: object[],
 *   disabled?: boolean,
 *   minRows?: number,
 *   helperText?: string,
 *   placeholder?: string,
 *   focusAtEndToken?: number,
 * }} props
 */
export default function TicketCommentEditor({
  label = 'Add a comment',
  value,
  onChange,
  mentionItems = [],
  disabled = false,
  minRows = 2,
  helperText,
  placeholder = 'Write a comment… Type @ to mention someone.',
  focusAtEndToken = 0,
}) {
  const theme = useTheme();
  const mentionItemsRef = useRef(mentionItems);
  mentionItemsRef.current = mentionItems;

  const suggestion = useMemo(
    () => createTicketMentionSuggestion(() => mentionItemsRef.current),
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        link: false,
      }),
      Placeholder.configure({ placeholder }),
      Mention.configure({
        HTMLAttributes: {
          class: 'ticket-mention-chip',
        },
        renderHTML({ options, node }) {
          return [
            'span',
            {
              ...options.HTMLAttributes,
              'data-type': 'mention',
              'data-id': node.attrs.id,
              'data-label': node.attrs.label,
            },
            `@${node.attrs.label ?? node.attrs.id}`,
          ];
        },
        suggestion,
      }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (current !== next) {
      editor.commands.setContent(next, false);
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor || !focusAtEndToken) return;
    editor.commands.focus('end');
  }, [editor, focusAtEndToken]);

  if (!editor) return null;

  return (
    <FormControl fullWidth disabled={disabled}>
      {label && (
        <FormLabel sx={{ mb: 0.75, fontSize: '0.875rem' }}>{label}</FormLabel>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          opacity: disabled ? 0.7 : 1,
        }}
      >
        <EditorContent editor={editor} className="ticket-comment-editor" />
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      <style>{`
        .ticket-comment-editor .ProseMirror {
          min-height: ${minRows * 28}px;
          padding: 12px 14px;
          outline: none;
          color: ${theme.palette.text.primary};
          font-family: ${theme.typography.body2.fontFamily};
          font-size: ${theme.typography.body2.fontSize};
          line-height: 1.6;
        }
        .ticket-comment-editor .ProseMirror p.is-editor-empty:first-child::before {
          color: ${theme.palette.text.disabled};
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ticket-comment-editor .ticket-mention-chip {
          color: ${theme.palette.primary.main};
          background: ${alpha(theme.palette.primary.main, 0.12)};
          border-radius: 4px;
          padding: 0 4px;
          font-weight: 600;
        }
      `}</style>
    </FormControl>
  );
}
